import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

const PUBLISHED = "2024-09-15";
const UPDATED = "2024-11-20";

const priceTable = [
  { type: "Combi boiler (like-for-like swap)", range: "£1,800 – £2,800", time: "1 day" },
  { type: "Combi boiler (new location)", range: "£2,500 – £3,500", time: "1 – 2 days" },
  { type: "System boiler replacement", range: "£2,200 – £3,400", time: "1 – 2 days" },
  { type: "Conventional (regular) boiler", range: "£2,400 – £3,800", time: "1 – 2 days" },
  { type: "Back boiler to combi conversion", range: "£3,200 – £4,500", time: "2 – 3 days" },
];

const factors = [
  "Boiler brand and model (Worcester Bosch, Vaillant, Ideal, Baxi)",
  "Whether the boiler is being moved to a new location",
  "Condition of existing pipework and radiators",
  "Power flush requirement (£300 – £600 extra)",
  "Magnetic system filter and smart thermostat add-ons",
  "Regional labour rates — London is typically 15–25% higher",
];

const tips = [
  "Get at least three written quotes from Gas Safe registered engineers.",
  "Ask whether the quote includes a power flush, magnetic filter and a thermostat.",
  "Check the manufacturer warranty length — 10 years is achievable with accredited installers.",
  "Avoid paying the full balance upfront; a deposit plus completion payment is standard.",
  "Confirm the Benchmark certificate and Gas Safe notification are included.",
];

const faqs = [
  {
    q: "How much does a new boiler cost in the UK in 2024?",
    a: "Most UK homeowners pay between £1,800 and £3,500 for a fully installed replacement combi boiler in 2024, including parts and labour. Larger system or regular boiler installs typically run £2,200 – £3,800.",
  },
  {
    q: "How long does a boiler replacement take?",
    a: "A straightforward like-for-like swap is usually completed in a single day. Relocating the boiler or converting from a back boiler to a combi can take 2 – 3 days.",
  },
  {
    q: "Is a power flush necessary?",
    a: "If your system is more than 8 – 10 years old or has cold spots on radiators, a power flush (£300 – £600) protects your new boiler and is often required to validate the manufacturer warranty.",
  },
  {
    q: "Can I find a Gas Safe engineer near me through Sortable?",
    a: "Yes — post your boiler replacement job on Sortable and you'll receive quotes from local Gas Safe registered heating engineers, with verified insurance and ratings.",
  },
];

export const BoilerReplacementCostPage = () => {
  const url = "https://sortable.uk/blog/cost-guides/boiler-replacement-cost-uk";

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: "Boiler Replacement Cost UK 2024: Average Prices & What to Expect",
        description:
          "How much does a new boiler cost in the UK in 2024? Average prices for combi, system and conventional boilers, factors that affect cost, and tips for getting the best quote near you.",
        datePublished: PUBLISHED,
        dateModified: UPDATED,
        author: { "@type": "Organization", name: "Sortable" },
        publisher: {
          "@type": "Organization",
          name: "Sortable",
          logo: { "@type": "ImageObject", url: "https://sortable.uk/favicon.png" },
        },
        mainEntityOfPage: url,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://sortable.uk/" },
          { "@type": "ListItem", position: 2, name: "Cost Guides", item: "https://sortable.uk/blog/cost-guides" },
          { "@type": "ListItem", position: 3, name: "Boiler Replacement Cost UK", item: url },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Boiler Replacement Cost UK 2024: Average Prices & Quotes | Sortable"
        description="How much does a new boiler cost in the UK in 2024? Average combi, system and regular boiler prices, what affects cost, and how to get accurate quotes near you."
        keywords="boiler replacement cost uk, new boiler cost, combi boiler price uk, boiler installation cost, gas safe engineer near me, boiler quote"
        canonical={url}
        ogType="article"
        structuredData={structuredData}
      />
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog/cost-guides" className="hover:text-primary">Cost Guides</Link>
          <span className="mx-2">/</span>
          <span>Boiler Replacement Cost UK</span>
        </nav>

        <article>
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Boiler Replacement Cost UK 2024: Average Prices & What to Expect
            </h1>
            <p className="text-sm text-muted-foreground">
              Published {PUBLISHED} · Updated {UPDATED}
            </p>
          </header>

          <p className="text-lg mb-6">
            The average cost of a new boiler in the UK in 2024 is <strong>£1,800 – £3,500</strong> fully fitted for a standard combi swap, rising to <strong>£4,500</strong> for more complex conversions. Prices vary by boiler brand, fuel type, location and the condition of your existing system.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-4">Average boiler replacement prices (2024)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-border rounded-lg">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3">Type of job</th>
                  <th className="text-left p-3">Typical cost</th>
                  <th className="text-left p-3">Time on site</th>
                </tr>
              </thead>
              <tbody>
                {priceTable.map((row) => (
                  <tr key={row.type} className="border-t border-border">
                    <td className="p-3">{row.type}</td>
                    <td className="p-3 font-medium">{row.range}</td>
                    <td className="p-3">{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Prices include parts and labour from a Gas Safe registered installer. VAT applies at standard rate.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-4">What affects the cost of a new boiler?</h2>
          <ul className="space-y-2">
            {factors.map((f) => (
              <li key={f} className="flex gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold mt-10 mb-4">Tips for getting the best quote</h2>
          <ol className="space-y-3 list-decimal list-inside">
            {tips.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ol>

          <Card className="mt-10 bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle>Get free quotes from Gas Safe engineers near you</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Post your boiler replacement on Sortable and receive comparable quotes from verified, insured local engineers — typically within hours.
              </p>
              <Button asChild>
                <Link to="/create-job">
                  Get free boiler quotes <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <h2 className="text-2xl font-bold mt-10 mb-4">Boiler replacement FAQs</h2>
          <div className="space-y-6">
            {faqs.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold mb-1">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-sm text-muted-foreground">
            Looking for more pricing benchmarks?{" "}
            <Link to="/blog/cost-guides" className="text-primary underline">
              Browse all UK tradesman cost guides
            </Link>
            .
          </div>
        </article>
      </main>
    </div>
  );
};

export default BoilerReplacementCostPage;
